import jwtDecode from 'jwt-decode';
import GeckoAuthSdk from '@geckolabs/gecko-auth-sdk';
import LocalStorageAdapter from '@geckolabs/gecko-auth-local-storage-adapter';
import GeckoStore from '../gecko-store';
import LocalGeckoAuth from './local.index';
import { setDefaultHeaders } from 'actions/appState';
import { onBootstrap } from '../bootstrap';

const instance = GeckoAuthSdk({
  storage: LocalStorageAdapter,
  baseUrl: process.env.REACT_APP_AUTH_API_URL,
});

let refreshPromise = null;

const GeckoAuth = {
  getDecodedToken: () => {
    return GeckoAuth.getToken().then(jwtDecode);
  },
  getToken: async () => {
    await GeckoAuth.refreshToken();
    return instance.TokenManager.fromStorage().then(
      (instance) => instance.AccessToken
    );
  },
  refreshToken: () => {
    // If there is already a refresh in flight use it
    if (refreshPromise) {
      return refreshPromise;
    }
    // Set a new refresh in flight
    refreshPromise = instance.TokenManager.refreshToken().finally(
      () => (refreshPromise = null)
    );
    // Return the inflight refresh
    return refreshPromise;
  },
  backupTokens: (backupKey) => {
    return instance.TokenManager.fromStorage().then((tokenInstance) => {
      const backup = instance.TokenManager.KEYS_TO_PERSIST.reduce(
        (backup, key) => {
          return Object.assign({}, backup, { [key]: tokenInstance[key] });
        },
        {}
      );
      GeckoStore.localStorage.set(backupKey, JSON.stringify(backup));
    });
  },
  hasBackup: (backupKey) => {
    return Boolean(GeckoStore.localStorage.get(backupKey));
  },
  restoreBackup: (backupKey) => {
    const backup = JSON.parse(GeckoStore.localStorage.get(backupKey));
    return GeckoAuth.setTokens(backup).then(() => {
      GeckoStore.localStorage.remove(backupKey);
    });
  },
  setTokens: (tokens) => {
    try {
      const tokenInstance = instance.TokenManager.fromData(tokens);
      return tokenInstance.persistToStorage();
    } catch (err) {
      return Promise.reject(err);
    }
  },
  claimCode: (code) => instance.TokenManager.claimCode(code),
  updateAuthHeaders: (token) => {
    const currentHeaders =
      GeckoStore.redux.get('appState.defaultHeaders') || {};
    if (currentHeaders.Authorization !== token) {
      return GeckoStore.redux.dispatch(
        setDefaultHeaders(
          Object.assign({}, currentHeaders, { Authorization: token })
        )
      );
    }
    return;
  },
};

const GeckoAuthService =
  process.env.NODE_ENV === 'development' ? LocalGeckoAuth : GeckoAuth;

onBootstrap(() => {
  const refreshInterval = 30 * 60 * 1000; // 30 minutes
  const intervalHandle = setInterval(
    () => GeckoAuthService.refreshToken(),
    refreshInterval
  );
  return () => clearInterval(intervalHandle);
});

export default GeckoAuthService;
